import { useAppDispatch, useAppSelector } from '../../../../../../App/redux/store';
import DateInputRHF from '../../../../../../shared/components/form/DateInputRHF';
import GenericForm from '../../../../../../shared/components/form/GenericForm';
import SelectRHF from '../../../../../../shared/components/form/SelectRHF';
import TextInputRHF from '../../../../../../shared/components/form/TextInputRHF';
import fetchAdDpeLocationsThunk from '../../../../services/thunks/fetchAdDpeLocationsThunk';
import styles from './adDetailsForms.module.scss';
import DpeFieldGroup from './DpeFieldGroup';
import FormWrapper from './FormWrapper';
import {
  adDetailsFormDatePickerLabelStyles,
  adDetailsFormDatePickerStyles,
  adDetailsFormSelectStyles,
  adDetailsFormTextInputStyles,
} from './muiStyles';
import SimilarAdDpeLoader from './SimilarAdDpeLoader';
import { dpeFormValidationSchema, LetterListEnum } from './validation';
import ValidationButton from './ValidationButton';
import { adActions, getAdState } from '../../../../adSlice';

function DpeForm({ ad }: { ad: Ad }) {
  const dispatch = useAppDispatch();
  const { adDetailsDpeFormValues } = useAppSelector(getAdState);

  const getDefaultValues = () => {
    return (
      adDetailsDpeFormValues || {
        dpe: (ad.dpe?.toUpperCase() as (typeof LetterListEnum)[number]) || null,
        ges: (ad.ges?.toUpperCase() as (typeof LetterListEnum)[number]) || null,
        landArea: ad.builtArea,
        dpeDateStart: new Date(2021, 6, 1).toISOString(), // 1 July 2021
        dpeDateEnd: new Date().toISOString(),
        builtAreaMin: ad.builtArea ?? null,
        builtAreaMax: ad.builtArea ?? null,
        typology: ad.propertyTypology.name,
      }
    );
  };

  const handleSubmit = (values: any) => {
    dispatch(
      fetchAdDpeLocationsThunk({
        minBuiltArea: values.builtAreaMin,
        maxBuiltArea: values.builtAreaMax,
        dpe: values.dpe || null,
        ges: values.ges || null,
        dpeStartDate: new Date(values.dpeDateStart).toISOString().split('T')[0],
        dpeEndDate: new Date(values.dpeDateEnd).toISOString().split('T')[0],
        propertyTypology: ad.propertyTypology.name,
        inseeCode: ad?.city.inseeCode ?? '',
      }),
    );
    dispatch(adActions.adDetailsDpeFormValuesSet(values));
  };

  const DpeFormWrapper = ({ children }: IChildren) => {
    return (
      <FormWrapper
        className={styles.dpeForm}
        title="Recherche par DPE et GES du bien"
      >
        {children}
      </FormWrapper>
    );
  };

  if (
    !(
      ad?.propertyTypology.name === 'Maison' ||
      ad?.propertyTypology.name === 'Appartement' ||
      ad?.propertyTypology.name === 'Bureaux' ||
      ad?.propertyTypology.name === 'Commerce' ||
      ad?.propertyTypology.name === 'Immeuble'
    )
  ) {
    return (
      <DpeFormWrapper>
        <p className={styles.disabledFormMessage}>
          La recherche par DPE est indisponible <br /> pour cette typologie de bien
        </p>
      </DpeFormWrapper>
    );
  }

  return (
    <DpeFormWrapper>
      <SimilarAdDpeLoader />
      <GenericForm
        defaultValues={getDefaultValues()}
        onSubmit={handleSubmit}
        schema={dpeFormValidationSchema}
      >
        <div className={styles.fieldContainer}>
          <DpeFieldGroup fieldName="builtAreaMax">
            <div className={styles.areaContainer}>
              <TextInputRHF
                type="number"
                name="builtAreaMin"
                variant="outlined"
                label="Surface du bien mini"
                placeholder="Surface du bien mini"
                classes={adDetailsFormTextInputStyles()}
              />
              <TextInputRHF
                type="number"
                name="builtAreaMax"
                variant="outlined"
                label="Surface du bien maxi"
                placeholder="Surface du bien maxi"
                classes={adDetailsFormTextInputStyles()}
              />
            </div>
          </DpeFieldGroup>

          <div className={styles.selectContainer}>
            <SelectRHF
              items={[...LetterListEnum]}
              name="dpe"
              noValueText="Aucun DPE"
              hasNoValue
              selectProps={{
                classes: adDetailsFormSelectStyles(),
                displayEmpty: true,
                label: 'DPE',
                placeholder: 'DPE',
              }}
            />
            <SelectRHF
              items={[...LetterListEnum]}
              name="ges"
              noValueText="Aucun GES"
              hasNoValue
              selectProps={{
                classes: adDetailsFormSelectStyles(),
                displayEmpty: true,
                label: 'GES',
                placeholder: 'GES',
              }}
            />
          </div>

          <DpeFieldGroup fieldName="dpeDateMaxi">
            <div className={styles.dateContainer}>
              <DateInputRHF
                name="dpeDateStart"
                fullWidth
                disableFuture
                label="Date de  DPE (début)"
                placeholder="Date de  DPE (début)"
                InputProps={{
                  classes: adDetailsFormDatePickerStyles(),
                }}
                InputLabelProps={{ classes: adDetailsFormDatePickerLabelStyles() }}
                clearable
                clearLabel="Effacer"
              />
              <DateInputRHF
                name="dpeDateEnd"
                fullWidth
                disableFuture
                label="Date de DPE (fin)"
                placeholder="Date de DPE (fin)"
                InputProps={{
                  classes: adDetailsFormDatePickerStyles(),
                }}
                InputLabelProps={{ classes: adDetailsFormDatePickerLabelStyles() }}
                clearable
                clearLabel="Effacer"
              />
            </div>
          </DpeFieldGroup>
        </div>

        <ValidationButton label="LANCER LA RECHERCHE PAR DPE" />
      </GenericForm>
    </DpeFormWrapper>
  );
}

export default DpeForm;
